import {setContext} from '@apollo/client/link/context';
import {onError} from '@apollo/client/link/error';
import {GraphQLError, GraphQLFormattedError} from 'graphql/error';
import {debounce, isString} from 'lodash';
import {toast} from 'sonner';
import {RequestHandler, Observable, FetchResult} from '@apollo/client';

import IntrospectionQueryMock from 'modules/decisionsNext/api/introspection.json';
import {IS_DEV_MODE} from 'config';
import {IMPERSONATION_HTTP_REQUEST_HEADER, impersonationToken} from 'core/impersonation';
import {setTokenAndAuthorized} from 'core/stores/auth';
import {refreshAuthToken} from 'core/utils/auth';
import {
  ApolloClient,
  ApolloLink,
  from,
  fromPromise,
  HttpLink,
  InMemoryCache,
  InMemoryCacheConfig,
} from 'utils/apolloWrapper';
import {EnnablLogger} from 'utils/ennabl';
import {showDefaultRequestErrorToast} from 'utils/toast';
import {getAuthorizationHeader, getFeatureOverridesHeader, getLogRocketLinkHeader} from 'utils/httpHeaders';

export {gql} from 'utils/apolloWrapper';

const isStatusCode401 = (error: any) => 'statusCode' in (error ?? {}) && error?.statusCode === 401;
const isAbortError = (error: any) => 'name' in (error ?? {}) && error?.name === 'AbortError';

let isTokenRefreshing = false;
let pendingRequests: Function[] = [];

const resolvePendingRequests = () => {
  pendingRequests.map(callback => callback());
  pendingRequests = [];
};

const refreshTokenOnExpireLink = onError(({networkError, operation, forward}) => {
  if (!isStatusCode401(networkError)) {
    return;
  }

  if (isTokenRefreshing) {
    return fromPromise(
      new Promise<void>(resolve => {
        pendingRequests.push(() => resolve());
      })
    ).flatMap(() => {
      return forward(operation);
    });
  }

  isTokenRefreshing = true;

  return fromPromise(
    refreshAuthToken()
      .catch(error => {
        EnnablLogger.error(['refreshTokenOnExpireLink', 'failed to refresh auth token'], {error});
        return null;
      })
      .finally(() => {
        isTokenRefreshing = false;
      })
  ).flatMap(authToken => {
    if (authToken) {
      EnnablLogger.log(['refreshTokenOnExpireLink', 'successfully refreshed auth token'], {authToken});
      setTokenAndAuthorized(authToken);
      resolvePendingRequests();
    } else {
      pendingRequests = [];
    }

    return forward(operation);
  });
});

const captureExceptionsLink = onError(({graphQLErrors}) => {
  if (!Array.isArray(graphQLErrors) || !graphQLErrors.length) {
    return;
  }

  graphQLErrors.forEach(error =>
    EnnablLogger.captureException(new Error(error.message, {cause: 'graphql error'}), {
      extra: {graphQLSchemaPath: error.path?.join(' -> ')},
    })
  );
});

const showDebouncedNetworkToast = debounce(() => {
  toast.error('Connection error', {
    description: 'Please check your internet connection and try again',
  });
}, 1000);

type AnyGraphQLError = GraphQLError | GraphQLFormattedError;

export type ValidationError = {
  /**
   * A list of errors for the field
   */
  constraints: Record<string, string>;
  /**
   * The field where the errors occurred
   */
  property: string;
};

const getGraphQLErrorExtensions = (error: AnyGraphQLError): Record<string, unknown> => {
  const extensions = error.extensions ?? {};
  const legacyException = extensions?.exception ?? {};

  return {...extensions, ...legacyException};
};

export const getValidationErrors = (error: AnyGraphQLError): ValidationError[] | undefined =>
  getGraphQLErrorExtensions(error)?.validationErrors as ValidationError[] | undefined;

export const getErrorStatusCode = (error: AnyGraphQLError): number | undefined =>
  getGraphQLErrorExtensions(error)?.statusCode as number | undefined;

const toastExceptionsLink = onError(({networkError, graphQLErrors}) => {
  if (isStatusCode401(networkError) || isAbortError(networkError)) {
    return;
  }

  if (graphQLErrors) {
    if (graphQLErrors.length !== 1) {
      return showDefaultRequestErrorToast();
    }

    const error = graphQLErrors[0];

    if (getValidationErrors(error)) {
      return showDefaultRequestErrorToast();
    }

    return error.message ? void toast.error(error.message) : showDefaultRequestErrorToast();
  }

  if (networkError) {
    if ('result' in networkError) {
      const message = isString(networkError.result) ? networkError.result : networkError.result.message;
      return message ? void toast.error(message) : showDefaultRequestErrorToast();
    }

    const isFetchError = ['Failed to fetch', 'Load failed'].some(error => networkError.message.includes(error));

    if (isFetchError) {
      return void showDebouncedNetworkToast();
    }

    return void showDefaultRequestErrorToast();
  }
});

const tokenLink = setContext((_, {headers}) => {
  const header = getAuthorizationHeader();

  if (!header) {
    return headers;
  }

  return {
    headers: {
      ...headers,
      [header.key]: header.value,
    },
  };
});

const logRocketLink = setContext((_, {headers}) => {
  const header = getLogRocketLinkHeader();

  if (!header) {
    return headers;
  }

  return {
    headers: {
      ...headers,
      [header.key]: header.value,
    },
  };
});

const introspectionLink: RequestHandler = (operation, forward) => {
  if (operation.operationName === 'IntrospectionQuery') {
    return new Observable<FetchResult>(observer => {
      observer.next({data: IntrospectionQueryMock});
      observer.complete();
    });
  }

  return forward(operation);
};

const passFeatureOverridesLink = setContext((_, {headers}) => {
  const header = getFeatureOverridesHeader();

  if (!header) {
    return headers;
  }

  return {
    headers: {
      ...headers,
      [header.key]: header.value,
    },
  };
});

export const graphQLClientFactory = (uri: string, cacheConfig: InMemoryCacheConfig = {}, links?: ApolloLink[]) => {
  const headers: Record<string, string> = {};

  if (impersonationToken) {
    headers[IMPERSONATION_HTTP_REQUEST_HEADER] = impersonationToken;
  }

  const httpLink = new HttpLink({
    uri,
    headers,
  });

  return new ApolloClient({
    devtools: {
      name: uri,
      enabled: IS_DEV_MODE,
    },
    cache: new InMemoryCache(cacheConfig),
    link: from(
      [
        IS_DEV_MODE && introspectionLink,
        ...(links ?? []),
        refreshTokenOnExpireLink,
        toastExceptionsLink,
        captureExceptionsLink,
        tokenLink,
        logRocketLink,
        passFeatureOverridesLink,
        httpLink,
      ].filter(Boolean)
    ),
  });
};
